import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, CreditCard, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
import { PaymentModal } from '../modals/PaymentModal';
import { useToast } from '../../hooks/useToast';
interface PaymentCardProps {
  amount: number;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue';
  onPay: () => void;
}
export function PaymentCard({
  amount,
  dueDate,
  status,
  onPay
}: PaymentCardProps) {
  const {
    toast
  } = useToast();
  const [showModal, setShowModal] = useState(false);
  const handleSuccess = () => {
    toast.success('Payment processed successfully!');
    setShowModal(false);
    onPay();
  };
  return <>
      <motion.div whileHover={{
      y: -2
    }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors" data-id="element-2798">
        <div className="flex justify-between items-start mb-4 gap-4" data-id="element-2799">
          <div className="min-w-0 flex-1" data-id="element-2800">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2801">
              Next Payment
            </p>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1 break-all" data-id="element-2802">
              {formatCurrency(amount)}
            </h3>
          </div>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize whitespace-nowrap flex-shrink-0 ${status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : status === 'overdue' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`} data-id="element-2803">
            {status}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-6 gap-2" data-id="element-2804">
          <Calendar className="h-4 w-4 flex-shrink-0" data-id="element-2805" />
          <span className="truncate" data-id="element-2806">
            Due{' '}
            {new Date(dueDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </span>
        </div>

        {status !== 'paid' && <div className="space-y-3" data-id="element-2807">
            <button onClick={() => setShowModal(true)} className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all hover:-translate-y-0.5" data-id="element-2808">
              <CreditCard className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-2809" />
              Pay Now
            </button>
            {status === 'overdue' && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex items-start p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 gap-2" data-id="element-2810">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" data-id="element-2811" />
                <p className="text-xs text-red-700 dark:text-red-300 break-words" data-id="element-2812">
                  Late fee of ₦5,000 may apply if not paid within 24 hours.
                </p>
              </motion.div>}
          </div>}

        {status === 'paid' && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center" data-id="element-2813">
            <p className="text-sm font-medium text-green-800 dark:text-green-300" data-id="element-2814">
              Payment Complete
            </p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1" data-id="element-2815">
              Thank you for your on-time payment!
            </p>
          </div>}
      </motion.div>

      <PaymentModal isOpen={showModal} onClose={() => setShowModal(false)} amount={amount} dueDate={dueDate} onSuccess={handleSuccess} data-id="element-2816" />
    </>;
}